import { Formik } from "formik";
import { FunctionComponent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "react-query";
import { toast } from "react-toastify";
import * as Yup from "yup";

import { CHANGES_APPLIED } from "@src/common/helpers/toastIds";
import useTestResults from "@src/modules/DeviceStatus/Devices/DeviceInfoView/ResultsView/hooks/useTestResults";
import useEditTestResults, {
  EditResultsModel,
} from "@src/modules/shared/components/Results/hooks/useEditTestResults";
import useStudyImages from "@src/modules/shared/components/Results/hooks/useStudyImages";
import ImagesRange from "@src/modules/shared/components/Results/ImagesRange";
import {
  DicomImages,
  ImageWrapper,
} from "@src/modules/shared/components/Results/Results.components";
import ResultsForm from "@src/modules/shared/components/Results/ResultsForm";
import SmallLoader from "@src/modules/shared/components/ScreenLoading/SmallLoader";
import { StudyTestResult, TestStatus } from "@src/modules/shared/types";
import {
  BaseBoxBody,
  BaseBoxHeader,
  StyledBoxWrapper,
} from "@src/modules/DeviceStatus/Devices/DeviceInfoView/ResultsView/ResultsView.components";
import ResultsFormHeader from "@src/modules/shared/components/Results/ResultsFormHeader";
import { useLocation } from "react-router";

const manualTestIds = [12301001];

export interface EditResultsFormValue {
  id: number;
  value?: number | boolean | string;
  isVirtual: boolean;
  notes?: string;
}

interface Props {
  selectedStudyId: string;
  selectedTest: StudyTestResult;
  isEditingAvailable: boolean;
  hasImageRangeEditing: boolean;
  hasDicoms: boolean;
  imagesDisabled?: boolean;
}

const Results: FunctionComponent<Props> = ({
  selectedStudyId,
  selectedTest,
  isEditingAvailable,
  hasImageRangeEditing,
  hasDicoms,
  imagesDisabled = false,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data: testResultsData, isLoading } = useTestResults(
    selectedStudyId,
    selectedTest.id,
  );
  const { search } = useLocation();
  const studyGroupId = new URLSearchParams(search).get("studyGroup");

  const [isEdit, setIsEdit] = useState<boolean>(
    manualTestIds.includes(selectedTest.testId) &&
      selectedTest.resultStatus === TestStatus.ActionNeeded,
  );

  const isInitialMount = useRef(true);

  const isNotes = testResultsData?.resultStatus === TestStatus.Notes;

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
    } else {
      setIsEdit(false);
    }
  }, [selectedTest.testId, selectedStudyId]);

  const editResultsMutation = useEditTestResults(
    selectedStudyId,
    selectedTest.id,
    studyGroupId,
    imagesDisabled,
  );

  const { data: studyImagesData } = useStudyImages(
    selectedStudyId,
    selectedTest.testId === 0
      ? selectedTest.id
      : selectedTest.testId.toString(),
  );

  const initialValues: EditResultsFormValue[] =
    testResultsData?.results.map(({ id, value, valueType, isVirtual }) => ({
      id,
      value: valueType === "Boolean" ? value === "1" : undefined,
      isVirtual,
    })) ?? [];

  const validationSchema: Yup.SchemaOf<EditResultsFormValue[]> = Yup.array()
    .of(
      Yup.object().shape({
        id: Yup.number().required(),
        isVirtual: Yup.mixed().optional(),
        value: Yup.mixed().test({
          test: (value) =>
            typeof value === "boolean" ||
            typeof value === "number" ||
            typeof value === "undefined",
        }),
        notes: Yup.string().optional(),
      }),
    )
    .required();

  const handleSubmit = (values: EditResultsFormValue[]) => {
    const model: EditResultsModel = values
      .filter(({ isVirtual }) => !isVirtual)
      .map(({ id, value, notes }) => ({
        id: id,
        value:
          typeof value === "number"
            ? value
            : typeof value === "boolean"
            ? value
              ? 1
              : 0
            : undefined,
        notes: notes,
      }));
    editResultsMutation.mutate(model, {
      onSuccess: () => {
        toast.success(t("Changes successfully saved"), {
          toastId: CHANGES_APPLIED,
        });
        setIsEdit(false);
        queryClient.invalidateQueries();
      },
    });
  };

  const images = studyImagesData?.map((base64, index) => (
    <ImageWrapper key={index}>
      <img
        src={`data:image/jpeg;base64,${base64}`}
        alt={`dicom image ${index}`}
      />
    </ImageWrapper>
  ));

  const hasImages = (images && images.length > 0) || hasDicoms;

  return (
    <StyledBoxWrapper>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize={true}
      >
        {(formProps) => (
          <>
            <BaseBoxHeader>
              <ResultsFormHeader
                selectedStudyId={selectedStudyId}
                selectedTest={selectedTest}
                isEditingAvailable={isEditingAvailable}
                isEdit={isEdit}
                setIsEdit={setIsEdit}
                {...formProps}
                onSave={formProps.submitForm}
                onCancel={formProps.resetForm}
              />
            </BaseBoxHeader>
            <BaseBoxBody>
              {isLoading && <SmallLoader />}
              {testResultsData && (
                <ResultsForm
                  {...formProps}
                  selectedStudyId={selectedStudyId}
                  selectedTest={selectedTest}
                  isEdit={isEdit}
                />
              )}
              {hasImageRangeEditing && hasImages && !isNotes && (
                <ImagesRange
                  selectedStudyId={selectedStudyId}
                  selectedTestId={selectedTest.id}
                />
              )}
              {!imagesDisabled && images && images.length > 0 && (
                <DicomImages>{images}</DicomImages>
              )}
            </BaseBoxBody>
          </>
        )}
      </Formik>
    </StyledBoxWrapper>
  );
};

export default Results;
